import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import Form from '../../components/Fields/Form';
import NumberField from '../../components/Fields/NumberField';
import SelectListField from '../../components/Fields/SelectListField';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from '../../components/Spinner/SpinnerOverlay';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import CodeTaskParameterInfo from '../../models/CodeTaskParameterInfo';
import WorkflowEventCodeTaskModel from '../../models/WorkflowEventCodeTaskModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { DataResult } from '../../types/Result';
import Unauthorized from '../Authentication/Unauthorized';
import CodeTaskField from './Controls/CodeTaskField';

const WorkflowEventCodeTaskView: React.FC = () => {
    const eventTypes = useSelectListOptions('api/v1/selectlist/WorkflowEventTypes', '-- Select event type');
    const codeTaskNames = useSelectListOptions('api/v1/selectlist/CodeTaskNames', '-- Select codetask handler');
    const [serverErrors, setServerErrors] = useState<DataResult<WorkflowEventCodeTaskModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException } = useNotifications();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);

    const [formData, setFormData] = useState<WorkflowEventCodeTaskModel>();

    let idParams: IdParams = useParams();
    let workflowEventCodeTaskId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        workflowEventCodeTaskId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (workflowEventCodeTaskId !== undefined) {
            DataService.getById<WorkflowEventCodeTaskModel>('api/v1/WorkflowEventCodeTasks/', workflowEventCodeTaskId)
                .then(response => {
                    setFormData(response);
                })
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [workflowEventCodeTaskId]);

    function submit() {
        if (formData) {
            DataService
                .update<WorkflowEventCodeTaskModel>(`api/v1/WorkflowEventCodeTasks/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        setFormData(response.data);
                })
                .catch(e => handleException(e));
        }
    }

    function setFieldValue(name: string, value: string) {
        if (formData) {
            name.split('.').reduce((object, propertyName, index, elements) => {
                if (index === elements.length - 1) {
                    object[propertyName] = value;
                    setFormData(() => { return { ...formData } });
                }

                if (object && object[propertyName]) {
                    return object[propertyName];
                }
                return null;
            }, formData);
        }
    }

    useEffect(() => {
        getCodeTaskParameterInfo(formData?.codeTaskName)
    }, [formData?.codeTaskName])

    function getCodeTaskParameterInfo(codeTaskName?: string) {
        if (formData && codeTaskName) {
            DataService
                .get<CodeTaskParameterInfo[]>(`api/v1/WorkflowEventCodeTasks/${workflowEventCodeTaskId}/CodeTaskParameterInfos/${codeTaskName}`)
                .then(response => {
                    for (let i = 0; i < response.length; i++) {
                        for (let j = 0; j < formData.codeTaskParameterInfos.length; j++) {
                            if (response[i].name == formData.codeTaskParameterInfos[j].name)
                                response[i].value = formData.codeTaskParameterInfos[j].value
                        }
                    }
                    formData.codeTaskParameterInfos = response

                    //console.log('formData', formData)
                    setFormData(() => { return { ...formData } })
                })
                .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'workflowEventTypeId', label: 'Event type', validationRules: [{ type: 'isRequired' }] },
            { name: 'executionOrder', label: 'Execution order', validationRules: [{ type: 'isRequired' }] },
            { name: 'codeTaskName', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'parameters', label: 'Parameters' },
        ],
        handleSubmit: submit,
        handleFieldChanged: setFieldValue,
        showErrors: 'always',
    };

    if (formData?.codeTaskParameterInfos) {
        formData.codeTaskParameterInfos.forEach((parInfo, index) => {
            formConfig.fields.push({ name: `codeTaskParameterInfos.${index}.value`, label: parInfo.name })
        })
    }

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    if (!workflowEventCodeTaskId || !formData)
        return <SpinnerOverlay />

    const { isAuthorizedForWorkflowDefinition } = useAuth();
    if (!isAuthorizedForWorkflowDefinition(ApplicationFunctions.ViewWorkflowDefinition, formData.workflowDefinitionId))
        return (<Unauthorized />);

    return (
        <div className='main-content'>
            <Form title='Workflow event codetask' footer={(
                <Row>
                    <Col md='12'>
                        <ButtonBar offset={2}>
                            <Authorize applicationFunction={ApplicationFunctions.UpdateWorkflowDefinition} /*workflowDefinitionId={formData.workflowDefinitionId}*/>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <SelectListField {...getFieldProps('workflowEventTypeId')} options={eventTypes} />
                            <NumberField {...getFieldProps('executionOrder')} />
                            <SelectListField {...getFieldProps('codeTaskName')} options={codeTaskNames} />
                            {
                                formData.codeTaskParameterInfos.map((parInfo, index) =>
                                    <CodeTaskField {...getFieldProps(`codeTaskParameterInfos.${index}.value`)} codeTaskParamterInfo={parInfo} key={parInfo.name} />
                                )
                            }
                        </Col>
                    </Row>
                </Container>
            </Form>
        </div>
    );
}
export default WorkflowEventCodeTaskView;